import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const fmt = (n: number) => n.toLocaleString();

export function Deflect() {
  const { data, isLoading, error } = useQuery({ queryKey: ["deflect"], queryFn: api.deflect });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const f = data.funnel;
  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" } as const;

  const stats: { label: string; value: string; sub?: string }[] = [
    { label: "Sessions", value: fmt(f.sessions), sub: f.period },
    { label: "Deflected", value: fmt(f.deflected), sub: "self-served" },
    { label: "Tickets created", value: fmt(f.ticketsCreated), sub: "reached a human" },
    { label: "Deflection rate", value: `${f.deflectionRate}%`, sub: `${f.trend >= 0 ? "+" : ""}${f.trend}pts vs prev` },
  ];

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Help widget</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Self-service deflection, the top articles doing the work, and the AI guardrails.
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        {stats.map((s) => (
          <div key={s.label} style={card}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6" }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, fontFamily: "'Space Mono',monospace", marginTop: 4 }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        {/* Top self-serve articles */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Icon name="book" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Top self-serve articles</h2>
          </div>
          {data.topArticles.map((a, i) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #f4f3ee" : "none" }}>
              <span style={{ fontSize: 11, fontFamily: "'Space Mono',monospace", color: "#a8aca6", width: 18, textAlign: "right" }}>{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                <div style={{ fontSize: 11, color: "#8a8e88" }}>{a.kind}{a.scope ? ` · ${a.scope}` : ""}</div>
              </div>
              <span style={{ fontSize: 11.5, fontFamily: "'Space Mono',monospace", color: "#4a504a" }}>{fmt(a.views)} views</span>
            </div>
          ))}
          {data.topArticles.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No published articles yet.</div>}
        </div>

        {/* Widget preview + guardrails */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 8 }}>Widget preview</div>
            <div style={{ border: "1px solid #e7e5df", borderRadius: 12, overflow: "hidden", boxShadow: "0 6px 20px rgba(0,0,0,.06)" }}>
              <div style={{ background: data.widget.accent, color: "#fff", padding: "12px 14px", fontSize: 13, fontWeight: 600 }}>{data.widget.greeting}</div>
              <div style={{ padding: 12 }}>
                <div style={{ border: "1px solid #e2e0d8", borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: "#8a8e88" }}>{data.widget.placeholder}</div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: data.widget.accent, border: `1px solid ${data.widget.accent}33`, borderRadius: 7, padding: "5px 10px" }}>{data.widget.handoffLabel}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Icon name="shield" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Guardrails</h2>
            </div>
            {data.guardrails.map((g, i) => (
              <div key={g.rule} style={{ display: "flex", gap: 9, padding: "8px 0", borderTop: i ? "1px solid #f4f3ee" : "none" }}>
                <Icon name={g.on ? "check-circle" : "circle"} size={14} color={g.on ? "#1f7a4d" : "#bcbfba"} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{g.rule}</div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{g.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
